import React from "react";
import { Loader2, Inbox } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export function Spinner({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center py-24 text-[#6E5B51]" data-testid="spinner">
      <Loader2 className="mr-2 animate-spin" size={22} /> {label}
    </div>
  );
}

export function Container({ children, className = "" }) {
  return <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>;
}

export function PageHeader({ eyebrow, title, subtitle, children }) {
  return (
    <div className="border-b border-border bg-gradient-to-b from-[#F4EFE6] to-[#FAF7F2]">
      <Container className="py-10 sm:py-14">
        {eyebrow && <p className="text-xs font-bold uppercase tracking-widest text-[#B04B26]">{eyebrow}</p>}
        <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-[#2D1E18] sm:text-4xl lg:text-5xl">{title}</h1>
        {subtitle && <p className="mt-3 max-w-2xl text-base leading-relaxed text-[#5C4A42]">{subtitle}</p>}
        {children}
      </Container>
    </div>
  );
}

export function EmptyState({ title, subtitle, icon: Icon = Inbox, children }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="empty-state">
      <Icon size={34} className="mx-auto text-[#C5B8A5]" />
      <p className="mt-3 font-display text-lg font-bold text-[#2D1E18]">{title}</p>
      {subtitle && <p className="mt-1 text-sm text-[#6E5B51]">{subtitle}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

export function ProtectedRoute({ children, staffOnly = false, adminOnly = false }) {
  const { user, isStaff } = useAuth();
  const location = useLocation();
  if (user === undefined) return <Spinner />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/admin" replace />;
  if (staffOnly && !isStaff) return <Navigate to="/dashboard" replace />;
  return children;
}
